import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { Outlet } from 'react-router'

import { escolherAdaptador, modoDeAuth } from '@/auth/adaptadores'
import type { AdaptadorDeAuth } from '@/auth/adaptadores/tipos'
import type { PoliticaDeSenha } from '@/auth/politicaDeSenha'
import {
  ContextoDeSessao,
  type ContextoSessao,
  type DesafioDeEntrada,
  type FalhaDePerfil,
  type ModoDeAuth,
  type MotivoSaida,
  type PerfilCarregado,
  type PerfilDaSessao,
  type Sessao,
} from '@/auth/session'
import {
  executarLimpezaDeSessao,
  registrarProvedorDeToken,
  registrarTratadorDeExpiracao,
} from '@/auth/sessionBridge'

interface SessionProviderProps {
  children?: ReactNode
  /** Test seam. Production always resolves the adapter from `VITE_AUTH_MODE`. */
  adaptador?: AdaptadorDeAuth
  /**
   * Busca `GET /eu` — a origem de `grupos` e `clientes` (§5.1).
   *
   * 🔒 **Injetada, não importada.** `api/clients/*` importa `auth/sessionBridge`; se
   * `auth/` importasse `api/`, os dois gateways do §5.5 ganhariam um ancestral comum
   * e a separação que o eslint protege morreria por baixo. O router é quem conhece os
   * dois lados, e é lá que a ligação é feita — visível, num lugar só.
   *
   * Ausente no modo mock, onde o adaptador já entrega as duas listas por fixture.
   */
  carregarPerfil?: (() => Promise<PerfilCarregado>) | undefined
}

/**
 * Owns the session and nothing else.
 *
 * It sits as a pathless layout route at the ROOT of the tree — above `/login` as well
 * as above the shell — because the login screen itself needs `entrar()`, and because
 * a provider mounted inside `AppShell` would be torn down and rebuilt on every
 * redirect to `/login`.
 *
 * Renders `children` when given them (tests, and any future non-router use) and an
 * `<Outlet/>` otherwise, so the same component serves both shapes.
 */
export function SessionProvider({ children, adaptador, carregarPerfil }: SessionProviderProps) {
  const [sessao, setSessao] = useState<Sessao | null>(null)
  const [carregando, setCarregando] = useState(true)
  const [avisoDeSaida, setAvisoDeSaida] = useState<MotivoSaida | null>(null)
  const [saidaDeliberada, setSaidaDeliberada] = useState(false)
  const [desafio, setDesafio] = useState<DesafioDeEntrada | null>(null)
  const [perfil, setPerfil] = useState<PerfilDaSessao | null>(null)
  const [erroDePerfil, setErroDePerfil] = useState<FalhaDePerfil | null>(null)

  const modo: ModoDeAuth = adaptador?.modo ?? modoDeAuth()

  // Held in a ref, not in state: `entrar`/`sair` need it without re-creating
  // themselves each render, and nothing renders differently because of it.
  const adaptadorRef = useRef<AdaptadorDeAuth | null>(adaptador ?? null)

  const resolverAdaptador = useCallback(async (): Promise<AdaptadorDeAuth> => {
    if (!adaptadorRef.current) {
      adaptadorRef.current = await escolherAdaptador(modo)
    }
    return adaptadorRef.current
  }, [modo])

  /**
   * Instala a sessão e, quando há de onde, o perfil que a completa.
   *
   * A identidade é instalada ANTES da requisição de perfil e permanece instalada se
   * ela falhar. Essa ordem é a decisão que importa: o usuário está autenticado de
   * fato, e derrubá-lo para `/login` por causa de um 403 da Administração o mandaria
   * repetir um login que funcionou e esconderia a causa real.
   *
   * ⚠️ Os ids que chegam do `/eu` SUBSTITUEM os do adaptador, nunca são mesclados. O
   * backend é a autoridade sobre permissão; unir as duas origens deixaria um grupo
   * removido lá continuar valendo aqui.
   */
  const aplicarSessao = useCallback(
    async (base: Sessao | null): Promise<void> => {
      if (!base) {
        setSessao(null)
        setPerfil(null)
        setErroDePerfil(null)
        return
      }

      if (!carregarPerfil) {
        setSessao(base)
        setPerfil(null)
        setErroDePerfil(null)
        return
      }

      /**
       * ⚠️ **A sessão é publicada INTEIRA, ou não é publicada.**
       *
       * Havia um `setSessao(base)` aqui, antes do `await`. Parecia inofensivo —
       * a identidade já está pronta, por que segurá-la? — e produzia um flash
       * real: o adaptador Cognito devolve `grupos: []` e `clientes: []` de
       * propósito (§5.1), então entre esta linha e a resposta do `/eu` o React
       * renderizava com uma sessão VÁLIDA e as duas listas VAZIAS.
       *
       * O usuário via, por uma fração de segundo depois do login:
       *   · "Nenhum cliente atribuído" — `DestinoDeCliente` não distingue
       *     "ainda carregando" de "não tem cliente nenhum";
       *   · "sem módulos" na sidebar, pela mesma razão.
       *
       * Duas telas que existem para descrever uma CONFIGURAÇÃO real, aparecendo
       * como estado de carregamento. Relatado pelo Fábio em 05/09/2026.
       *
       * No boot isso nunca apareceu porque `carregando` cobre a janela e
       * `RotaProtegida` mostra a `TelaDeEspera`. No LOGIN não há essa cobertura:
       * `entrar()` não volta a `carregando`, e não deveria — a correção é não
       * existir estado intermediário para cobrir.
       *
       * 🔒 O token NÃO vem daqui: `carregarPerfil` usa o provedor registrado em
       * `sessionBridge`, alimentado pelo ADAPTADOR. Segurar a sessão não deixa a
       * própria busca sem credencial — foi o que tornou esta correção possível.
       */
      try {
        const carregado = await carregarPerfil()
        setSessao({
          ...base,
          grupos: carregado.usuario.grupos,
          clientes: carregado.usuario.clientes,
        })
        setPerfil({ grupos: carregado.grupos, clientes: carregado.clientes })
        setErroDePerfil(null)
      } catch (erro) {
        // 🔑 A sessão entra AQUI, e é obrigatória: `RotaProtegida` manda para
        // `/login` quando não há sessão, e checa isso ANTES de `erroDePerfil`.
        // Sem esta linha, uma falha de perfil devolveria o usuário ao login em
        // vez da tela `SemPerfil`, que é a que explica o que aconteceu.
        setSessao(base)
        // A mensagem já vem em pt-BR de `carregarPerfilDaApi`; o genérico cobre um
        // lançamento inesperado, que não deve virar uma tela muda.
        setPerfil(null)
        setErroDePerfil({
          mensagem:
            erro instanceof Error && erro.message !== ''
              ? erro.message
              : 'Não foi possível carregar suas permissões.',
          // Um erro que não seja `ErroDePerfil` é imprevisto; oferecer a repetição é
          // a escolha generosa, porque o custo de um clique inútil é menor que o de
          // esconder a única saída de uma falha transitória.
          podeTentarNovamente:
            erro instanceof Error && 'podeTentarNovamente' in erro
              ? Boolean((erro as { podeTentarNovamente: unknown }).podeTentarNovamente)
              : true,
        })
      }
    },
    [carregarPerfil],
  )

  // ── Boot: restore whatever session already exists ──────────────────────────────
  useEffect(() => {
    let vivo = true

    void (async () => {
      try {
        const atual = await resolverAdaptador()
        const restaurada = await atual.restaurar()
        // `carregando` só cai depois do perfil (o `finally` abaixo). Liberar a árvore
        // com a identidade pronta e os grupos ainda vazios faria toda tela guardada
        // piscar "sem permissão" antes de aparecer.
        if (vivo) await aplicarSessao(restaurada)
      } catch (erro) {
        // A restore that fails is "no session", not a broken app. Logged rather than
        // surfaced: the user is about to see the login screen, which explains itself.
        console.error('Falha ao restaurar a sessão:', erro)
        if (vivo) await aplicarSessao(null)
      } finally {
        if (vivo) setCarregando(false)
      }
    })()

    return () => {
      vivo = false
    }
  }, [resolverAdaptador, aplicarSessao])

  const sair = useCallback(async (motivo: MotivoSaida = 'usuario') => {
    try {
      await adaptadorRef.current?.sair()
    } finally {
      // AC-10 — the order matters. The session goes first so no component can
      // re-fetch into a cache that is about to be wiped; the registered cleanups
      // (query cache in 0.6, persisted tenant in 0.5) run after.
      setSessao(null)
      setPerfil(null)
      setErroDePerfil(null)
      setDesafio(null)
      setAvisoDeSaida(motivo === 'expirada' ? 'expirada' : null)
      // 🔒 O caminho onde o usuário anterior estava deixa de ser um destino
      // válido: ele carrega o `idCliente` DELE. Ver `RotaProtegida`.
      setSaidaDeliberada(motivo === 'usuario')
      await executarLimpezaDeSessao()
    }
  }, [])

  const entrar = useCallback(
    async (email: string, senha: string) => {
      const atual = await resolverAdaptador()
      const resultado = await atual.entrar(email, senha)
      setAvisoDeSaida(null)
      setSaidaDeliberada(false)

      // Correct credentials on a never-used account do not produce a session: an
      // invited user starts in FORCE_CHANGE_PASSWORD and must choose a password
      // first. `/login` switches step; it does not navigate, because the challenge
      // lives in the adapter's memory and would not survive a reload.
      if (resultado.tipo === 'novaSenhaRequerida') {
        setDesafio('novaSenha')
        return
      }

      setDesafio(null)
      await aplicarSessao(resultado.sessao)
    },
    [resolverAdaptador, aplicarSessao],
  )

  const definirNovaSenha = useCallback(
    async (novaSenha: string) => {
      const atual = await resolverAdaptador()
      const nova = await atual.definirNovaSenha(novaSenha)
      setDesafio(null)
      await aplicarSessao(nova)
    },
    [resolverAdaptador, aplicarSessao],
  )

  const politicaDeSenha = useCallback(async (): Promise<PoliticaDeSenha> => {
    const atual = await resolverAdaptador()
    return atual.politicaDeSenha()
  }, [resolverAdaptador])

  const solicitarCodigoDeRedefinicao = useCallback(
    async (email: string) => {
      const atual = await resolverAdaptador()
      await atual.solicitarCodigoDeRedefinicao(email)
    },
    [resolverAdaptador],
  )

  const confirmarRedefinicaoDeSenha = useCallback(
    async (email: string, codigo: string, novaSenha: string) => {
      const atual = await resolverAdaptador()
      await atual.confirmarRedefinicaoDeSenha(email, codigo, novaSenha)
      // No `setSessao` here, deliberately: the pool leaves the user signed out after a
      // reset. The login screen shows a success notice and asks for the new password.
    },
    [resolverAdaptador],
  )

  /**
   * Nova tentativa de `GET /eu` sem obrigar o usuário a entrar de novo.
   *
   * A falha mais provável é temporária (gateway fora do ar, rede) e a sessão continua
   * válida — mandá-lo repetir o login não consertaria nada e ainda pareceria culpa
   * dele. O 403 de conta sem cadastro é o caso em que isto não resolve, e a tela diz
   * isso com todas as letras em vez de oferecer só um botão de tentar de novo.
   */
  const recarregarPerfil = useCallback(async () => {
    if (!sessao) return
    await aplicarSessao(sessao)
  }, [sessao, aplicarSessao])

  // ── Bridge: what lives outside React ──────────────────────────────────────────
  useEffect(() => {
    // AC-4 — story 0.6 plugs this into the request interceptor of BOTH API clients.
    return registrarProvedorDeToken(async () => {
      const atual = await resolverAdaptador()
      return atual.obterToken()
    })
  }, [resolverAdaptador])

  useEffect(() => {
    // AC-5 — story 0.6's 401 handler calls `notificarSessaoExpirada()`, which lands
    // here and produces a redirect with a readable message instead of a raw 401.
    return registrarTratadorDeExpiracao(() => {
      void sair('expirada')
    })
  }, [sair])

  const valor = useMemo<ContextoSessao>(
    () => ({
      sessao,
      perfil,
      erroDePerfil,
      recarregarPerfil,
      carregando,
      avisoDeSaida,
      saidaDeliberada,
      desafio,
      entrar,
      definirNovaSenha,
      cancelarDesafio: () => setDesafio(null),
      solicitarCodigoDeRedefinicao,
      confirmarRedefinicaoDeSenha,
      politicaDeSenha,
      sair,
      descartarAviso: () => setAvisoDeSaida(null),
      modo,
    }),
    [
      sessao,
      perfil,
      erroDePerfil,
      recarregarPerfil,
      carregando,
      avisoDeSaida,
      saidaDeliberada,
      desafio,
      entrar,
      definirNovaSenha,
      solicitarCodigoDeRedefinicao,
      confirmarRedefinicaoDeSenha,
      politicaDeSenha,
      sair,
      modo,
    ],
  )

  return (
    <ContextoDeSessao.Provider value={valor}>{children ?? <Outlet />}</ContextoDeSessao.Provider>
  )
}
